import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePerson, useUpdatePerson, useChangePersonStatus } from '../../../platform/api/hooks';
import { asRecord, str, asList } from '../../../platform/api/utils';
import { usePermissions } from '../../../platform/auth/permissions';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  type PersonForm,
  type Tab,
  STATUS_LABELS,
  STATUS_VARIANTS,
  personFormSchema,
  SkeletonDetail,
  primaryBtn,
  secondaryBtn,
  linkBtn,
} from './pessoa-detail.support';
import {
  PessoaDadosTab,
  PessoaCargosTab,
  PessoaVinculoTab,
  PessoaHistoricoTab,
} from './pessoa-detail.dados-tab';

export function PessoaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: person, isLoading, isError } = usePerson(id ?? '');
  const updatePerson = useUpdatePerson();
  const changeStatus = useChangePersonStatus();
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('dados');
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const p = asRecord(person);
  const st = str(p.status);

  const memberships = (p.memberships as Record<string, unknown>[] | undefined) ?? [];
  const currentMembership = memberships[0];
  const history = asList(p.statusHistory ?? []);

  const { can } = usePermissions();

  const form = useForm<PersonForm>({
    resolver: zodResolver(personFormSchema),
    values: {
      civilName: str(p.civilName),
      socialName: str(p.socialName),
      religiousName: str(p.religiousName),
      oriki: str(p.oriki),
      email: str(p.email),
      phone: str(p.phone),
      birthDate: typeof p.birthDate === 'string' ? p.birthDate.substring(0, 10) : '',
    },
  });

  const functionAssignments = asList(p.functionAssignments);

  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>
            Não foi possível carregar os dados desta pessoa.
          </p>
          <button
            type="button"
            onClick={() => {
              void navigate('/pessoas');
            }}
            style={linkBtn}
          >
            ← Voltar para pessoas
          </button>
        </Section>
      </div>
    );
  }

  async function handleSave(data: PersonForm) {
    if (!id) return;
    const { oriki, ...rest } = data;
    try {
      await updatePerson.mutateAsync({
        id,
        data: can('people.oriki.update') ? { ...rest, oriki } : rest,
      });
      setEditing(false);
    } catch {
      // The mutation error is rendered in the detail header.
    }
  }

  async function handleStatusChange() {
    if (!newStatus || !id) return;
    try {
      await changeStatus.mutateAsync({ id, data: { status: newStatus } });
      setStatusChangeOpen(false);
      setNewStatus('');
    } catch {
      // The mutation error is rendered in the detail header.
    }
  }

  const nome = str(p.socialName ?? p.civilName);
  const mutationError = updatePerson.error ?? changeStatus.error;

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/pessoas');
        }}
        style={linkBtn}
      >
        ← Voltar para pessoas
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{nome}</h2>
          {str(p.religiousName) && (
            <p style={{ fontSize: '0.8rem', color: 'var(--faint)', margin: '0.25rem 0 0' }}>
              {str(p.religiousName)}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Badge label={STATUS_LABELS[st] ?? st} variant={STATUS_VARIANTS[st] ?? 'default'} />
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
              }}
              style={secondaryBtn}
            >
              Editar
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setStatusChangeOpen(true);
              }}
              style={{
                ...secondaryBtn,
                background: 'var(--warning-soft)',
                borderColor: 'var(--warning)',
                color: 'var(--warning)',
              }}
            >
              Alterar status
            </button>
          )}
        </div>
      </div>

      {mutationError && (
        <p
          role="alert"
          style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: '0 0 1rem' }}
        >
          {mutationError instanceof Error && mutationError.message
            ? mutationError.message
            : 'Não foi possível salvar a alteração. Tente novamente.'}
        </p>
      )}

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: '1rem',
          borderBottom: '2px solid var(--line)',
        }}
      >
        {(
          [
            ['dados', 'Dados básicos'],
            ['vinculo', 'Vínculo'],
            ['cargos', 'Cargos'],
            ['historico', 'Histórico'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setActiveTab(key);
            }}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: activeTab === key ? 600 : 400,
              color: activeTab === key ? 'var(--green)' : 'var(--muted)',
              border: 'none',
              borderBottom: activeTab === key ? '2px solid var(--green)' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Modal: Alterar status */}
        {statusChangeOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'var(--overlay)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 14,
                padding: '1.5rem',
                minWidth: 320,
                maxWidth: 400,
              }}
            >
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Alterar status</h3>
              <select
                value={newStatus}
                onChange={(e) => {
                  setNewStatus(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  fontSize: '0.875rem',
                  marginBottom: '1rem',
                }}
              >
                <option value="">Selecione...</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v} disabled={v === st}>
                    {l}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setStatusChangeOpen(false);
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleStatusChange();
                  }}
                  disabled={!newStatus || changeStatus.isPending}
                  style={primaryBtn}
                >
                  {changeStatus.isPending ? 'Salvando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Dados básicos */}
        {activeTab === 'dados' && (
          <PessoaDadosTab
            editing={editing}
            form={form}
            p={p}
            updatePending={updatePerson.isPending}
            onCancelEdit={() => {
              setEditing(false);
            }}
            onSave={(data) => {
              void handleSave(data);
            }}
          />
        )}
        {activeTab === 'cargos' && <PessoaCargosTab functionAssignments={functionAssignments} />}
        {activeTab === 'vinculo' && <PessoaVinculoTab currentMembership={currentMembership} />}
        {activeTab === 'historico' && <PessoaHistoricoTab history={history} />}
      </div>
    </div>
  );
}
